import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Star, CircleDollarSign, ClipboardList, FileText, LifeBuoy, User as UserIcon, Activity, ChevronRight, AlertCircle, Radio, Wallet, RefreshCw, Users, PackageCheck, FlaskConical } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { getApiErrorMessage } from '@/lib/network/apiErrors';
import { useNetwork } from '@/lib/network/NetworkProvider';
import BottomNav from '@/components/BottomNav';
import useProviderLocationHeartbeat from '@/hooks/useProviderLocationHeartbeat';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { canSelectAvailability, formatProviderAvailability } from '@/lib/providerDispatch';
import { buildProviderActiveOrders } from '@/lib/providerActiveOrders';

const AVAIL_KEYS = ['available', 'busy', 'offline'];

const ACTIVE_BOOKING_POLL_MS = 3000;

const EMPTY_EARNINGS = {
  today: 0,
  monthly: 0,
  lifetime: 0,
  completed_count: 0,
  total_available: 0,
  wallet: {},
};

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isOnline } = useNetwork();
  const [provider, setProvider] = useState(null);
  const [criticalState, setCriticalState] = useState('loading'); // loading | error | ready
  const [criticalError, setCriticalError] = useState('');
  const [earnings, setEarnings] = useState(EMPTY_EARNINGS);
  const [reviews, setReviews] = useState({ average: 0, total: 0 });
  const [orders, setOrders] = useState([]);
  const [activeBooking, setActiveBooking] = useState(null);
  const [marketplaceOrders, setMarketplaceOrders] = useState({ pharmacy: [], labs: [], deliveries: [] });
  const [activeOrdersLoading, setActiveOrdersLoading] = useState(true);
  const [activeOrdersError, setActiveOrdersError] = useState('');
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [secondaryErrors, setSecondaryErrors] = useState({});

  const loadActiveOrders = useCallback(async (prov, silent = false) => {
    if (!prov) return;
    if (!silent) setActiveOrdersLoading(true);

    const tasks = [
      { key: 'booking', run: () => api.get('/providers/me/active-booking') },
    ];
    if (prov.category === 'pharmacy') {
      tasks.push({ key: 'pharmacy', run: () => api.get('/providers/me/pharmacy/orders') });
    }
    if (prov.category === 'lab_test') {
      tasks.push({ key: 'labs', run: () => api.get('/providers/me/lab/orders') });
    }
    if (prov.delivery_enabled) {
      tasks.push({ key: 'deliveries', run: () => api.get('/providers/me/pharmacy/deliveries') });
    }

    const results = await Promise.allSettled(tasks.map((task) => task.run()));
    const updates = {};
    const failures = [];
    results.forEach((result, index) => {
      const key = tasks[index].key;
      if (result.status !== 'fulfilled') {
        failures.push(result.reason);
        return;
      }
      const data = result.value.data || {};
      if (key === 'booking') {
        // A successful null response clears a finished booking; a failed request
        // leaves the last known booking card visible during temporary outages.
        setActiveBooking(data.booking || null);
      } else {
        updates[key] = Array.isArray(data.orders) ? data.orders : [];
      }
    });

    setMarketplaceOrders((previous) => ({
      pharmacy: prov.category === 'pharmacy' ? (updates.pharmacy ?? previous.pharmacy) : [],
      labs: prov.category === 'lab_test' ? (updates.labs ?? previous.labs) : [],
      deliveries: prov.delivery_enabled ? (updates.deliveries ?? previous.deliveries) : [],
    }));
    setActiveOrdersError(
      failures.length
        ? getApiErrorMessage(failures[0], 'Could not refresh active orders')
        : '',
    );
    if (!silent) setActiveOrdersLoading(false);
  }, []);

  const loadSecondary = useCallback(async () => {
    setSecondaryLoading(true);
    setSecondaryErrors({});
    const errors = {};

    const tasks = [
      {
        key: 'earnings',
        run: () => api.get('/providers/me/earnings'),
        onOk: (data) => {
          setEarnings({
            today: data.today ?? 0,
            monthly: data.monthly ?? 0,
            lifetime: data.lifetime ?? 0,
            completed_count: data.completed_count ?? 0,
            total_available: data.total_available ?? data.lifetime ?? 0,
            wallet: data.wallet || {},
          });
        },
      },
      {
        key: 'reviews',
        run: () => api.get('/providers/me/reviews'),
        onOk: (data) => setReviews(data),
      },
      {
        key: 'orders',
        run: () => api.get('/providers/me/orders'),
        onOk: (data) => setOrders(data.orders || []),
      },
      {
        key: 'wallet',
        run: () => api.get('/providers/me/wallet'),
        onOk: (data) => {
          if (data.wallet) {
            setEarnings((prev) => ({ ...prev, wallet: data.wallet }));
          }
        },
      },
    ];

    const results = await Promise.allSettled(tasks.map((t) => t.run()));
    results.forEach((result, index) => {
      const task = tasks[index];
      if (result.status === 'fulfilled') {
        task.onOk(result.value.data);
      } else {
        errors[task.key] = getApiErrorMessage(
          result.reason,
          'Could not load this section',
        );
      }
    });

    setSecondaryErrors(errors);
    setSecondaryLoading(false);
  }, []);

  const loadCritical = useCallback(async () => {
    setCriticalState('loading');
    setCriticalError('');
    try {
      const { data } = await api.get('/providers/me');
      const prov = data.provider;
      if (!prov) {
        setCriticalState('error');
        setCriticalError('Provider profile not found.');
        return;
      }
      setProvider(prov);
      setCriticalState('ready');
      if (prov.approval_status !== 'approved') {
        navigate('/provider/status', { replace: true });
        return;
      }
      await loadSecondary();
    } catch (e) {
      setCriticalState('error');
      setCriticalError(getApiErrorMessage(e, 'Could not load your Ranger profile'));
    }
  }, [loadSecondary, navigate]);

  useEffect(() => {
    loadCritical();
  }, [loadCritical]);

  // P0-B: Re-fetch authoritative state when the page regains focus/visibility
  // so that mutations performed on other screens (e.g. cancellation) are reflected
  // without requiring a full app restart.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        loadSecondary();
        loadActiveOrders(provider, true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [loadSecondary, loadActiveOrders, provider]);

  // Poll all relevant order sources so an accepted and paid order appears without
  // restarting the app. Failed requests never clear a previously visible card.
  useEffect(() => {
    if (criticalState !== 'ready' || !provider) return undefined;
    let cancelled = false;
    let inFlight = false;
    const poll = async (silent = true) => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        await loadActiveOrders(provider, silent);
      } finally {
        inFlight = false;
      }
    };
    poll(false);
    const timer = setInterval(() => poll(true), ACTIVE_BOOKING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [criticalState, provider, loadActiveOrders]);

  useProviderLocationHeartbeat(provider?.availability_status);

  const setAvail = async (status) => {
    if (!canSelectAvailability(provider, status)) {
      toast.error('Complete your active booking before going available');
      return;
    }
    try {
      await api.patch('/providers/me/availability', { availability_status: status });
      setProvider({ ...provider, availability_status: status });
      toast.success(`Status set to ${status}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (criticalState === 'loading') {
    return (
      <div className="resqly-shell">
        <div className="resqly-frame flex items-center justify-center min-h-screen text-slate-500">
          Loading...
        </div>
      </div>
    );
  }

  if (criticalState === 'error' || !provider) {
    return (
      <div className="resqly-shell">
        <div className="resqly-frame flex flex-col items-center justify-center min-h-screen px-6 text-center">
          <AlertCircle className="w-10 h-10 text-amber-600 mb-3" />
          <p className="text-slate-900 font-semibold">Could not open Ranger dashboard</p>
          <p className="text-sm text-slate-600 mt-2 max-w-xs">{criticalError}</p>
          {!isOnline && (
            <p className="text-xs text-slate-500 mt-2">You appear to be offline.</p>
          )}
          <Button className="mt-4" onClick={loadCritical}>
            <RefreshCw className="w-4 h-4 mr-1" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  const availMeta = formatProviderAvailability(provider.availability_status);
  const availColors = {
    available: 'bg-emerald-500',
    busy: 'bg-amber-500',
    offline: 'bg-slate-400',
  };
  const financialSectionError = secondaryErrors.earnings || secondaryErrors.wallet;

  const activeOrderCards = buildProviderActiveOrders({
    activeBooking,
    provider,
    pharmacyOrders: marketplaceOrders.pharmacy,
    labOrders: marketplaceOrders.labs,
    deliveryOrders: marketplaceOrders.deliveries,
  });

  return (
    <div className="resqly-shell">
      <div className="resqly-frame flex flex-col min-h-screen" data-testid="provider-dashboard">
        <div className="gradient-blue text-white px-5 pt-6 pb-7 relative">
          <div className="flex items-center justify-between">
            <Logo size={26} withText={false} />
            <button onClick={() => navigate('/provider/profile')} className="p-2 bg-white/20 rounded-full"><UserIcon className="w-5 h-5" /></button>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-white text-blue-700 font-bold flex items-center justify-center text-xl">
              {(provider.name || 'R').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-lg">{provider.name || 'Welcome'}</div>
              <div className="text-xs text-blue-100 capitalize flex items-center gap-2">
                {provider.category?.replace('_', ' ')}
                <span className="inline-flex items-center gap-1 bg-white/15 px-1.5 py-0.5 rounded">
                  <Star className="w-3 h-3 fill-amber-300 text-amber-300" />
                  {reviews.average || provider.rating || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Persistent Active Orders section: booking, pharmacy, lab and Ranger delivery orders */}
        <section className="px-5 mt-4 relative z-20" data-testid="provider-active-orders">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-slate-900">Active Orders</h3>
            <button
              type="button"
              aria-label="Refresh active orders"
              onClick={() => loadActiveOrders(provider)}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-500"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          {activeOrderCards.length > 0 ? (
            <div className="space-y-2">
              {activeOrderCards.map((order) => (
                <button
                  type="button"
                  key={order.key}
                  data-testid={`provider-active-order-card-${order.type}`}
                  onClick={() => navigate(order.route)}
                  className="w-full text-left resqly-card p-4 border-blue-200 bg-gradient-to-br from-blue-50 to-white flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">{order.title}</div>
                    <div className="font-semibold text-slate-900 mt-1">#{String(order.id).slice(0, 8)}</div>
                    <div className="text-xs text-slate-600 mt-0.5 capitalize">{order.status}</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-blue-700 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="resqly-card p-4">
              <p className="text-sm font-semibold text-slate-800">
                {activeOrdersLoading ? 'Checking for active orders…' : 'No active orders right now'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Accepted and paid orders will appear here automatically.
              </p>
            </div>
          )}
          {activeOrdersError && (
            <div className="mt-2">
              <SectionError message={activeOrdersError} onRetry={() => loadActiveOrders(provider)} testid="dashboard-active-orders-error" />
            </div>
          )}
        </section>

        <div className="px-5 mt-4 relative z-10">
          <div className="resqly-card p-5 overflow-visible">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 font-medium">{['pharmacy','lab_test'].includes(provider?.category) ? 'Marketplace availability' : 'Availability Status'}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`pulse-dot ${availColors[provider.availability_status] || 'bg-slate-400'}`}
                    data-testid="provider-availability-dot"
                  />
                  <span className="text-lg font-bold text-slate-900" data-testid="provider-availability-status">
                    {['pharmacy','lab_test'].includes(provider?.category) ? 'Accepting concurrent orders' : availMeta.label}
                  </span>
                </div>
              </div>
              <Activity className="w-8 h-8 text-blue-700" />
            </div>
            {!(['pharmacy','lab_test'].includes(provider?.category)) && <div className="grid grid-cols-3 gap-2 mt-4">
              {AVAIL_KEYS.map((key) => {
                const meta = formatProviderAvailability(key);
                const disabled = !canSelectAvailability(provider, key);
                return (
                  <button
                    data-testid={`avail-${key}`}
                    key={key}
                    onClick={() => setAvail(key)}
                    disabled={disabled}
                    className={`py-2.5 rounded-lg text-sm font-semibold border ${
                      provider.availability_status === key
                        ? 'border-blue-700 bg-blue-50 text-blue-800'
                        : 'border-slate-200 bg-white text-slate-700'
                    } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>}
            <p className="text-xs text-slate-500 mt-2">{['pharmacy','lab_test'].includes(provider?.category) ? 'This marketplace does not use a busy/offline blocker. Your team can quote and accept multiple orders concurrently.' : availMeta.desc}</p>
            {provider.financial_locked && (
              <p className="text-xs text-red-700 mt-1" data-testid="provider-financial-locked">
                Orders paused. Recharge to continue receiving orders.
              </p>
            )}
            {earnings.wallet?.recharge_balance != null && earnings.wallet.recharge_balance <= (earnings.wallet.active_plan?.recharge_amount || 0) * 0.2 && earnings.wallet.recharge_balance > 0 && (
              <p className="text-xs text-amber-700 mt-1" data-testid="provider-low-balance">
                Low recharge balance
              </p>
            )}
          </div>
        </div>

        {financialSectionError && (
          <div className="px-5 mt-4">
            <SectionError
              message={financialSectionError}
              onRetry={loadSecondary}
              testid="dashboard-financial-error"
            />
          </div>
        )}

        <div className="px-5 mt-4 grid grid-cols-2 gap-3">
          <Kpi icon={<CircleDollarSign className="text-blue-600" />} label="Today's Earnings" value={secondaryLoading ? '…' : `₹${earnings.today}`} />
          <Kpi icon={<CircleDollarSign className="text-emerald-600" />} label="Total Available" value={secondaryLoading ? '…' : `₹${earnings.total_available ?? earnings.lifetime}`} />
          <Kpi icon={<Wallet className="text-indigo-600" />} label="Recharge Balance" value={secondaryLoading ? '…' : `₹${earnings.wallet?.recharge_balance ?? 0}`} />
          <Button className="h-auto py-3" variant="outline" onClick={() => navigate('/provider/plans')} data-testid="recharge-cta">
            Recharge
          </Button>
        </div>

        {secondaryErrors.orders && (
          <div className="px-5 mt-3">
            <SectionError message={secondaryErrors.orders} onRetry={loadSecondary} testid="dashboard-orders-error" />
          </div>
        )}

        <div className="px-5 mt-4 grid grid-cols-2 gap-3">
          <Kpi icon={<ClipboardList className="text-blue-600" />} label="Total Orders" value={secondaryLoading ? '…' : orders.length} />
          <Kpi icon={<Star className="text-amber-500" />} label="Avg Rating" value={secondaryErrors.reviews ? '—' : (reviews.average || provider.rating || 0)} />
          <Kpi icon={<UserIcon className="text-blue-600" />} label="Profile" value={`${provider.profile_completion}%`} />
        </div>

        <div className="px-5 mt-5">
          <h3 className="font-semibold text-slate-900 mb-2">Quick Actions</h3>
          <div className="resqly-card divide-y">
            <Action testid="qa-referral" icon={<Users className="w-4 h-4 text-blue-600" />} label="Refer & Grow" onClick={() => navigate('/provider/referral')} />
            <Action testid="qa-offers" icon={<Radio className="w-4 h-4 text-blue-600" />} label="Service Offers" onClick={() => navigate('/provider/offers')} />
            <Action testid="qa-pharmacy" icon={<PackageCheck className="w-4 h-4 text-emerald-600" />} label="Pharmacy & Ranger Delivery" onClick={() => navigate('/provider/pharmacy')} />
            {provider?.category === 'lab_test' && <Action testid="qa-labs" icon={<FlaskConical className="w-4 h-4 text-violet-600" />} label="Lab Quotes & Orders" onClick={() => navigate('/provider/labs')} />}
            <Action testid="qa-orders" icon={<ClipboardList className="w-4 h-4 text-blue-600" />} label="Orders" onClick={() => navigate('/provider/orders')} />
            <Action testid="qa-earnings" icon={<CircleDollarSign className="w-4 h-4 text-blue-600" />} label="Earnings" onClick={() => navigate('/provider/earnings')} />
            <Action testid="qa-reviews" icon={<Star className="w-4 h-4 text-amber-500" />} label="Reviews" onClick={() => navigate('/provider/reviews')} />
            <Action testid="qa-documents" icon={<FileText className="w-4 h-4 text-slate-600" />} label="Documents" onClick={() => navigate('/provider/documents')} />
            <Action testid="qa-support" icon={<LifeBuoy className="w-4 h-4 text-blue-600" />} label="Support" onClick={() => navigate('/provider/support')} />
            <Action testid="qa-profile" icon={<UserIcon className="w-4 h-4 text-blue-600" />} label="Profile" onClick={() => navigate('/provider/profile')} />
          </div>
        </div>

        {provider.profile_completion < 100 && (
          <div className="px-5 mt-5">
            <div className="resqly-card p-4 bg-amber-50/60 border-amber-100 flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-slate-900">Complete your profile</div>
                <div className="text-xs text-slate-600">Your profile is {provider.profile_completion}% complete. Add bank details, languages and specializations to attract more customers.</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1" />
        <BottomNav variant="provider" />
      </div>
    </div>
  );
}

function SectionError({ message, onRetry, testid }) {
  return (
    <div className="resqly-card p-3 bg-amber-50 border-amber-100 text-sm" data-testid={testid}>
      <p className="text-amber-900">{message}</p>
      <button type="button" onClick={onRetry} className="text-blue-700 font-semibold text-xs mt-2 inline-flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Retry
      </button>
    </div>
  );
}

function Kpi({ icon, label, value }) {
  return (
    <div className="resqly-card p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500">{label}</span>
        <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">{icon}</div>
      </div>
      <div className="text-lg font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}
function Action({ icon, label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-slate-50">
      <div className="w-7 h-7 rounded-md bg-slate-50 flex items-center justify-center">{icon}</div>
      <span className="text-sm font-medium text-slate-800">{label}</span>
      <ChevronRight className="w-4 h-4 text-slate-300 ml-auto" />
    </button>
  );
}
